import { DetailTab } from '@/features/openTabs/components/AccountDetail/parts/DetailTabs'
import {
    useOpenAccountCatalog,
    useOpenAccountSettlement,
    useOpenTabs,
} from '@/features/openTabs/contexts/OpenTabsData'
import { useOpenTabHeader } from '@/features/openTabs/hooks/useOpenTabHeader'
import { useRenameOpenTab } from '@/features/openTabs/hooks/useRenameOpenTab'
import { useSubmittedOpenTabItemActions } from '@/features/openTabs/hooks/useSubmittedOpenTabItemActions'
import { AccountStatus } from '@/features/openTabs/types'
import { getAccountPriceBreakdown } from '@/features/openTabs/utils/accountCalculations'
import { toAccountHistoryEventView } from '@/features/openTabs/utils/history/viewModels'
import {
    formatAccountListPrice,
    formatAccountPrice,
    getAccountTotalPrice,
    toAccountListItem,
    toAccountPriceItems,
    toOrderView,
} from '@/features/openTabs/utils/viewModels'
import {
    useConfigurableProductId,
    useConfigurableProductIdActions,
} from '@/features/pos/contexts/ConfigurableProductId'
import { useIntervaledState } from '@/hooks/useIntervalState'
import { useRouter } from 'expo-router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Keyboard } from 'react-native'

export type AccountModalMode = 'create' | 'rename' | null

const useOpenTabsPage = () => {
    const { t } = useTranslation('app')
    const router = useRouter()
    const configuredProductId = useConfigurableProductId()
    const { set: configureProduct, unset: closeProductConfiguration } =
        useConfigurableProductIdActions()
    const {
        openTabs,
        closedAccounts,
        activeStatus,
        selectedAccount,
        selectedAccountSummary,
        selectedAccountId,
        currency,
        loading,
        detailLoading,
        listFailed,
        retry,
        refreshAccount,
        detailFailed,
        detailRefreshFailed,
        creatingAccount,
        openTabsTotal,
        closedAccountsTotal,
        paymentMethods,
        setActiveStatus,
        selectAccount,
        clearSelection,
        createAccount,
    } = useOpenTabs()
    const {
        catalogLoading,
        catalogFailed,
        catalogCartRefreshFailed,
        retryCatalogCart,
        addingCatalogItem,
        submittingCatalogCart,
        catalogCategories,
        catalogSections,
        catalogCartProductIds,
        catalogCartItems,
        editingCatalogItemId,
        catalogCartTotalAmount,
        catalogCartPriceBreakdown,
        catalogCartTotalQuantity,
        editCatalogItem,
        stopEditingCatalogItem,
        removeCatalogItem,
        changeCatalogItemQuantity,
        clearCatalogCart,
        submitCatalogCart,
    } = useOpenAccountCatalog()
    const { clearPaymentSelection, prepareFullPayment } = useOpenAccountSettlement()

    const [activeDetailTab, setActiveDetailTab] = useState<DetailTab>('items')
    useEffect(() => setActiveDetailTab('items'), [selectedAccountId])

    const [modalMode, setModalMode] = useState<AccountModalMode>(null)
    const [accountName, setAccountName] = useState('')
    const renameAccountIdRef = useRef<string | null>(null)
    const renameRequestRef = useRef(false)
    const { renameAccount, renamingAccount } = useRenameOpenTab({
        accountId: selectedAccount?.id,
        isOpen:
            selectedAccount?.status === 'open' &&
            !detailLoading &&
            !detailFailed &&
            !detailRefreshFailed,
        refreshAccount,
    })
    const [catalogMode, setCatalogMode] = useState(false)
    const [compactCatalogReview, setCompactCatalogReview] = useState(false)
    const [selectedCategoryId, setSelectedCategoryId] = useState('')
    const submittedItemActions = useSubmittedOpenTabItemActions({
        accountId: selectedAccount?.id,
        isOpen:
            selectedAccount?.status === 'open' &&
            !catalogMode &&
            !detailLoading &&
            !detailFailed &&
            !detailRefreshFailed,
        refreshAccount,
        onSuccess: clearPaymentSelection,
    })

    useEffect(() => {
        if (
            modalMode === 'rename' &&
            (renameAccountIdRef.current !== selectedAccount?.id ||
                selectedAccount?.status !== 'open')
        ) {
            renameAccountIdRef.current = null
            setModalMode(null)
            setAccountName('')
        }
    }, [modalMode, selectedAccount?.id, selectedAccount?.status])

    const handleSubmittedItemQuantityChange = (itemId: string, change: -1 | 1) => {
        const item = selectedAccount?.orders
            .flatMap(({ items }) => items)
            .find(({ id }) => id === itemId)
        if (item && item.settledQuantity !== undefined) {
            void submittedItemActions.changeQuantity(
                { ...item, settledQuantity: item.settledQuantity },
                change
            )
        }
    }
    const handleSubmittedItemRemove = (itemId: string) => {
        const item = selectedAccount?.orders
            .flatMap(({ items }) => items)
            .find(({ id }) => id === itemId)
        if (item && item.settledQuantity !== undefined) {
            void submittedItemActions.removeItem({ ...item, settledQuantity: item.settledQuantity })
        }
    }

    useEffect(() => {
        if (
            catalogCategories.length > 0 &&
            !catalogCategories.some(({ id }) => id === selectedCategoryId)
        ) {
            setSelectedCategoryId(catalogCategories[0].id)
        }
    }, [catalogCategories, selectedCategoryId])

    const accounts = activeStatus === 'open' ? openTabs : closedAccounts
    const accountsTotal = activeStatus === 'open' ? openTabsTotal : closedAccountsTotal
    const accountItems = useMemo(() => accounts.map(toAccountListItem), [accounts])
    const orders = useMemo(
        () => selectedAccount?.orders.map((order) => toOrderView(order, t)) ?? [],
        [selectedAccount, t]
    )
    const events = useIntervaledState(
        useCallback(
            () =>
                selectedAccount?.history.map((event) =>
                    toAccountHistoryEventView(event, t, selectedAccount.currency)
                ) ?? [],
            [selectedAccount, t]
        ),
        60000
    )
    const catalogTotalPrice = formatAccountPrice(catalogCartTotalAmount, currency)
    const catalogPriceItems = toAccountPriceItems(catalogCartPriceBreakdown, currency, t)
    const selectedAccountPriceItems = toAccountPriceItems(
        getAccountPriceBreakdown(selectedAccount),
        selectedAccount?.currency ?? currency,
        t
    )
    const canSettleAccount = paymentMethods.length > 0

    const resetCatalog = useCallback(() => {
        setCatalogMode(false)
        setCompactCatalogReview(false)
        clearCatalogCart()
        closeProductConfiguration()
    }, [clearCatalogCart, closeProductConfiguration])

    useEffect(() => {
        if (
            catalogMode &&
            (!selectedAccountId ||
                selectedAccountSummary?.status === 'closed' ||
                selectedAccount?.status === 'closed')
        ) {
            resetCatalog()
        }
    }, [
        catalogMode,
        resetCatalog,
        selectedAccountId,
        selectedAccountSummary?.status,
        selectedAccount?.status,
    ])

    const handleStatusChange = useCallback(
        (status: AccountStatus) => {
            setActiveStatus(status)
            resetCatalog()
        },
        [resetCatalog, setActiveStatus]
    )

    const handleAccountSelect = useCallback(
        (accountId: string) => {
            selectAccount(accountId)
            resetCatalog()
        },
        [resetCatalog, selectAccount]
    )

    const openCreateModal = useCallback(() => {
        if (renameRequestRef.current) return

        renameAccountIdRef.current = null
        setAccountName('')
        setModalMode('create')
    }, [])

    const openRenameModal = () => {
        if (
            selectedAccount?.status !== 'open' ||
            detailLoading ||
            detailFailed ||
            creatingAccount ||
            renameRequestRef.current
        )
            return

        renameAccountIdRef.current = selectedAccount.id
        setAccountName(selectedAccount.name)
        setModalMode('rename')
    }

    const closeModal = () => {
        if (creatingAccount || renameRequestRef.current) return

        renameAccountIdRef.current = null
        setModalMode(null)
        setAccountName('')
    }

    const handleModalConfirm = async () => {
        if (creatingAccount || renameRequestRef.current) return

        if (modalMode === 'rename') {
            const accountId = renameAccountIdRef.current
            if (!accountId) return

            renameRequestRef.current = true
            try {
                const renamed = await renameAccount(accountId, accountName)
                if (renamed && renameAccountIdRef.current === accountId) {
                    renameAccountIdRef.current = null
                    setModalMode(null)
                    setAccountName('')
                }
            } finally {
                renameRequestRef.current = false
            }
            return
        }

        if (modalMode !== 'create') return

        const createdAccountId = await createAccount(accountName)
        if (!createdAccountId) return

        setModalMode(null)
        setAccountName('')
    }

    const handleProductSelect = (productId: string) => {
        if (addingCatalogItem || submittingCatalogCart) return
        Keyboard.dismiss()
        stopEditingCatalogItem()
        configureProduct(productId)
    }

    const handleDraftItemEdit = (itemId: string) => {
        Keyboard.dismiss()
        closeProductConfiguration()
        editCatalogItem(itemId)
    }

    const handleCatalogConfirm = async () => {
        if (!selectedAccount || addingCatalogItem || submittingCatalogCart) return

        const submitted = await submitCatalogCart(selectedAccount.id)
        if (submitted) resetCatalog()
    }

    const openPayAll = () => {
        if (
            !selectedAccount ||
            detailLoading ||
            detailFailed ||
            !canSettleAccount ||
            submittedItemActions.saving
        )
            return

        if (prepareFullPayment())
            router.push({ pathname: '/open-tabs/payment', params: { mode: 'all' } })
    }

    const openSplit = () => {
        if (
            !selectedAccount ||
            detailLoading ||
            detailFailed ||
            !canSettleAccount ||
            submittedItemActions.saving
        )
            return

        clearPaymentSelection()
        router.push('/open-tabs/split')
    }

    const handleDetailClose = () => {
        if (submittingCatalogCart) return
        clearSelection()
        resetCatalog()
    }

    useOpenTabHeader(
        selectedAccountId
            ? {
                  title:
                      (selectedAccountSummary?.status ?? activeStatus) === 'closed'
                          ? t('openTabs.navigation.closedAccounts')
                          : t('drawerItem.openTabs'),
                  onBack: () => {
                      if (modalMode) {
                          closeModal()
                          return
                      }
                      if (submittingCatalogCart || (addingCatalogItem && !catalogCartRefreshFailed))
                          return
                      if (configuredProductId || editingCatalogItemId) {
                          closeProductConfiguration()
                          stopEditingCatalogItem()
                          return
                      }
                      if (catalogMode) {
                          if (compactCatalogReview) setCompactCatalogReview(false)
                          else resetCatalog()
                      } else handleDetailClose()
                  },
              }
            : undefined
    )

    return {
        activeDetailTab,
        setActiveDetailTab,
        events,
        accountItems,
        accountsTotal: formatAccountListPrice(accountsTotal, currency),
        activeStatus,
        accountName,
        catalogCategories,
        catalogSections,
        catalogMode,
        compactCatalogReview,
        setCompactCatalogReview,
        catalogCartItems,
        handleDraftItemEdit,
        removeCatalogItem,
        changeCatalogItemQuantity,
        submittedItemsBusy: submittedItemActions.saving,
        submittedItemsRefreshFailed: submittedItemActions.refreshFailed,
        retrySubmittedItemsRefresh: submittedItemActions.retryRefresh,
        handleSubmittedItemQuantityChange,
        handleSubmittedItemRemove,
        catalogTotalPrice,
        catalogPriceItems,
        catalogLoading,
        catalogCartRefreshFailed,
        retryCatalogCart,
        listFailed,
        retry,
        detailFailed,
        detailRefreshFailed,
        catalogFailed,
        creatingAccount,
        renamingAccount,
        detailLoading,
        loading,
        submittingCatalogCart,
        catalogBusy: addingCatalogItem || submittingCatalogCart,
        closedAccountCount: closedAccounts.length,
        modalMode,
        openAccountCount: openTabs.length,
        orders,
        selectedAccount,
        selectedAccountSummary,
        selectedAccountId,
        selectedAccountPriceItems,
        selectedCategoryId,
        selectedProductIds: catalogCartProductIds,
        closeModal,
        handleAccountSelect,
        handleCatalogConfirm,
        handleDetailClose,
        handleModalConfirm,
        handleProductSelect,
        handleStatusChange,
        openCreateModal,
        openRenameModal,
        resetCatalog,
        setAccountName,
        setCatalogMode,
        setSelectedCategoryId,
        openPayAll,
        openSplit,
        catalogHasSelection: catalogCartTotalQuantity > 0,
        canSettleAccount,
        selectedAccountTotal: selectedAccount ? getAccountTotalPrice(selectedAccount) : undefined,
    }
}

export default useOpenTabsPage
